import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsExporting from 'highcharts/modules/exporting';
import highchartsExportData from 'highcharts/modules/export-data';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';
import highchartsBoost from 'highcharts/modules/boost';
import { useAppContext } from '../../contexts/AppContext';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Backdrop, Box, CircularProgress, Typography } from '@mui/material';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import { colors, plotLines } from '../../models/Models';
import { getYearlyDays } from '../../utils/utils';
dayjs.extend(dayOfYear);

highchartsExporting(Highcharts);
highchartsExportData(Highcharts);
noDataToDisplay(Highcharts);
highchartsBoost(Highcharts);

type Props = {
  data: any;
  ymin: number;
  ymax: number;
};

const Chart = ({ data, ymin, ymax }: Props) => {
  const { isDarkMode } = useAppContext();
  const [chartOptions, setChartOptions] = useState<Highcharts.Options | null>(null);
  const chartComponent = useRef(null);
  const isLoading = false;

  // Memoize formatted categories to avoid recalculating on every render
  const formattedCategories = useMemo(() => {
    if (!data?.times) return [];
    return data.times.map((x: string) => {
      let d = new Date(Date.parse(x));
      return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
    });
  }, [data?.times]);

  // Memoize plotLines for rain and cleaning events
  const plotLinesData = useMemo(() => {
    if (!data?.times) return [];

    const lines: any[] = [];

    // Add rain event plotLines
    if (data?.rain_dates?.length) {
      const rainIndices = getYearlyDays(data.rain_dates)
        .map((val) => dayjs(val).format('YYYY-MM-DD'))
        .map((ymd) => data?.times.findIndex((t: string) => dayjs(new Date(Date.parse(t))).format('YYYY-MM-DD') === ymd))
        .filter((idx: number) => idx >= 0);

      rainIndices.forEach((idx: number) => {
        lines.push({
          label: {
            text: 'Rain',
            style: { fontSize: '10px' },
          },
          color: 'rgba(0, 0, 255, 0.7)',
          value: idx,
          width: 2,
        });
      });
    }

    return lines;
  }, [data?.times, data?.rain_dates, data?.cleaning_dates]);

  // Memoize prepared data
  const preparedData = useMemo(() => {
    if (!data?.hasOwnProperty('values') || !data?.hasOwnProperty('times')) {
      return null;
    }

    // Prepare data inline to avoid hoisting issues
    let timeseries_data: any[] = [];
    let i = 0;
    Object.keys(data?.values).forEach((key) => {
      i = plotLines.hasOwnProperty(key) ? i : i + 1;

      let name = plotLines.hasOwnProperty(key)
        ? (plotLines as any)[key].label
        : key.includes('_production')
        ? 'Production using ' + key.substring(0, key.lastIndexOf('_production'))
        : key.includes('combined_soiling_ratio')
        ? 'Soiling Ratio'
        : key;

      // Skip cleaning_border series
      if (key === 'cleaning_border') {
        return;
      }

      // Handle cleaning data with alternating colors
      let seriesData = data?.values[key];
      let seriesColor = plotLines.hasOwnProperty(key) ? (plotLines as any)[key].color : colors[i];

      if (key === 'cleaning') {
        // Apply alternating colors for cleaning data based on value
        seriesData = data?.values[key].map((value: any) => {
          if (value !== 0 && value !== null && value !== undefined) {
            return {
              y: 1, // Always use 1 for height, color will differentiate
              color: value === 1 ? '#FF6B35' : value === 2 ? '#C55824' : value === 3 ? '#808080' : '#b75cf8ff',
              borderWidth: 0, // Disable border for cleaning bars
            };
          }
          return value;
        });
      }

      timeseries_data.push({
        name: name,
        data: seriesData,
        color: seriesColor,
        type: key === 'rain' || key === 'cleaning' ? 'column' : 'line',
        zIndex: key === 'rain' || key === 'cleaning' ? -10 : null,
        borderWidth: key === 'cleaning' ? 0 : undefined, // Disable border for cleaning series
        pointPadding: key === 'cleaning' ? 0 : undefined,
        groupPadding: key === 'cleaning' ? 0 : undefined,
        // yAxis: key.includes('production') ? 1 : 0
      });
    });
    return timeseries_data;
  }, [data]);

  useEffect(() => {
    if (preparedData) {
      prepareHighcharts(preparedData);
    }
  }, [preparedData, formattedCategories, plotLinesData, isDarkMode]);

  const prepareHighcharts = (highChartsData: any) => {
    const chart: Highcharts.Options = {
      chart: {
        type: 'line',
        // @ts-expect-error Highcharts type missing zoomType in this setup
        zoomType: 'x',
      },
      title: {
        text: data?.title, // (' + Math.round(data?.average_soiling_ratio*1000)/1000 + ')',
        align: 'left',
      },
      // Enable boost module for better performance with large datasets
      boost: {
        useGPUTranslations: true,
        usePreallocated: true,
      },
      xAxis: {
        // type: 'datetime',
        categories: formattedCategories,
        title: { text: 'Date' },
        gridLineWidth: 1,
        plotLines: plotLinesData,
      },
      yAxis: [
        {
          title: { text: data.xTitle },
          ...(Object.keys(data?.values).some((key) => key.includes('soiling_ratio'))
            ? {
                min: ymin,
                max: ymax,
                tickInterval: 0.2, // Tick marks every 0.2
                startOnTick: true, // Start on a tick mark
                endOnTick: true, // End on a tick mark
                allowDecimals: true,
              }
            : {}),
        },
      ],
      legend: {
        enabled: true,
      },
      tooltip: {
        valueDecimals: 4,
        shared: false, // Changed from true - shared tooltips are slow with many series
        split: false,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        series: {
          // Disable markers by default for better performance
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 3,
              },
            },
          },
          // Enable turboThreshold for large datasets
          turboThreshold: 10000,
          // Reduce animation time
          animation: {
            duration: 500,
          },
        },
        line: {
          marker: {
            enabled: false,
          },
          lineWidth: 2,
        },
        area: {
          stacking: 'normal' as Highcharts.OptionsStackingValue,
          marker: {
            enabled: false,
          },
          lineWidth: 2,
          lineColor: '#000000',
          fillOpacity: 0.7,
        },
        column: {
          pointPadding: 0,
          groupPadding: 0,
          borderWidth: 0, // Reduced from 1 for better performance
          enableMouseTracking: true,
        },
      },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
        csv: {
          dateFormat: '%Y-%m-%d',
        },
      },
      series: highChartsData,
    };
    setChartOptions(chart);
  };

  return (
    <>
      <Box sx={{ justifyContent: 'center', pl: 1, pr: 1 }}>
        {isLoading ? (
          <Backdrop open={true} sx={{ width: '100%', height: '450px', position: 'relative' }}>
            <CircularProgress />
          </Backdrop>
        ) : chartOptions ? (
          <HighchartsReact
            ref={chartComponent}
            containerProps={{ className: isDarkMode ? 'dark-chart' : '', style: { width: '100%' } }}
            highcharts={Highcharts}
            options={chartOptions}
          />
        ) : (
          <Typography color="text.secondary">Loading chart...</Typography>
        )}
      </Box>
    </>
  );
};

export default Chart;
